import React from 'react';
import { Image, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { ReviewPhoto } from '../../data/reviews';
import { colors, hairline, radii, spacing } from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * The photographs on a review: what is attached, and how to attach more.
 *
 * Used twice with different powers - in the composer, where photos can be
 * added and removed, and on the reviews list, where they can only be taken
 * down. `onAdd` decides which: no handler, no add tile.
 *
 * ---------------------------------------------------------------------------
 * TWO TILES, NOT ONE BUTTON AND A MENU
 * ---------------------------------------------------------------------------
 * Camera and library are separate tiles side by side. The alternative - one
 * "Add" that opens a chooser - costs a tap in both directions and hides which
 * options exist until you commit to finding out. There are exactly two
 * sources and both fit, so both are shown.
 *
 * The camera sits first because on a review it is the likelier one: the job
 * just finished and the thing being photographed is in the room.
 *
 * ---------------------------------------------------------------------------
 * THE CAP IS SHOWN, NOT ENFORCED SILENTLY
 * ---------------------------------------------------------------------------
 * "2 of 5" sits beside the strip from the first photograph, and the add tile
 * disappears when the last slot is filled. A picker that simply stops opening
 * reads as a bug; a counter that was there all along reads as a rule.
 *
 * ---------------------------------------------------------------------------
 * A THUMBNAIL WITH NO FILE IS DRAWN AS ONE ANYWAY
 * ---------------------------------------------------------------------------
 * The seeded reviews carry photographs that do not exist on disk - there is no
 * uploads server - so their `uri` is empty. Rather than a broken image, those
 * fall back to a tinted tile with a picture glyph, which is honestly "a photo
 * is attached here" without pretending to show it.
 */

/** Side of one thumbnail. Two rows of five fit a phone at this size. */
const TILE = 72;

type Props = {
  photos: ReviewPhoto[];
  /** Ceiling from the platform setting - see `REVIEW_MAX_PHOTOS`. */
  max: number;
  /** Opens the library picker. Omitted on a read-only strip. */
  onAdd?: () => void;
  /**
   * Opens the camera. Omitted on a read-only strip, and independently of
   * `onAdd` - a caller that only wants the library keeps working unchanged.
   */
  onCapture?: () => void;
  /**
   * Removes one before the review is sent.
   *
   * Different from taking a published photo down: nothing has been shared yet,
   * so this needs no confirmation and no record. The list screen passes
   * `onHide` instead.
   */
  onRemove?: (photo: ReviewPhoto) => void;
  /** Takes a published photo down. Confirmed by the caller. */
  onHide?: (photo: ReviewPhoto) => void;
};

function PhotoStrip({
  photos,
  max,
  onAdd,
  onCapture,
  onRemove,
  onHide,
}: Props) {
  const shown = photos.filter(photo => !photo.hidden);
  const full = shown.length >= max;
  /* Either source counts as "this strip accepts photographs", which is what
     the counter's hint and the empty-strip copy are actually about. */
  const canAdd = !!onAdd || !!onCapture;

  return (
    <View style={styles.wrap}>
      <View style={styles.head}>
        <Text variant="labelSm" color={colors.inkBody}>
          Photos
        </Text>
        {/* Only once there is something to count. "0 of 5" on an empty strip
            reads as a requirement rather than a limit. */}
        {shown.length > 0 ? (
          <Text variant="caption" color={colors.inkMuted}>
            {shown.length} of {max}
          </Text>
        ) : null}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.rail}
      >
        {shown.map(photo => (
          <View key={photo.id} style={styles.tile}>
            {photo.uri ? (
              <Image source={{ uri: photo.uri }} style={styles.art} />
            ) : (
              <View style={[styles.art, styles.artFallback]}>
                <Icon name="image" size={22} color={colors.inkMuted} />
              </View>
            )}

            {/* Over the corner of its own thumbnail, so which photo it removes
                is never in question. */}
            {onRemove || onHide ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={onHide ? 'Remove this photo' : 'Remove photo'}
                hitSlop={8}
                onPress={() => (onHide ? onHide(photo) : onRemove?.(photo))}
                style={({ pressed }) => [
                  styles.remove,
                  pressed ? styles.pressed : null,
                ]}
              >
                <Icon
                  name="plus"
                  size={13}
                  color={colors.onDanger}
                  strokeWidth={2.4}
                  style={styles.removeGlyph}
                />
              </Pressable>
            ) : null}
          </View>
        ))}

        {onCapture && !full ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Take a photo, ${shown.length} of ${max} used`}
            onPress={onCapture}
            style={({ pressed }) => [
              styles.add,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon name="camera" size={20} color={colors.primary} />
            <Text variant="caption" color={colors.primary}>
              Camera
            </Text>
          </Pressable>
        ) : null}

        {onAdd && !full ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Choose a photo, ${shown.length} of ${max} used`}
            onPress={onAdd}
            style={({ pressed }) => [
              styles.add,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon name="image" size={20} color={colors.primary} />
            <Text variant="caption" color={colors.primary}>
              Upload
            </Text>
          </Pressable>
        ) : null}
      </ScrollView>

      {canAdd ? (
        <Text variant="caption" color={colors.inkMuted}>
          {full
            ? `That is all ${max}. Remove one to swap it.`
            : 'Show the finished work - it makes your review far more useful.'}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rail: {
    gap: spacing.sm,
    // Room for the remove badge, which overhangs the top-right of each tile.
    paddingTop: spacing.xs,
    paddingRight: spacing.xs,
  },
  tile: {
    width: TILE,
    height: TILE,
  },
  art: {
    width: TILE,
    height: TILE,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  artFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    ...hairline,
  },
  /* A rotated plus: the icon set has no cross, and 45 degrees is exactly one -
     the same trick the sheets use for their close control. */
  remove: {
    position: 'absolute',
    top: -spacing.xs,
    right: -spacing.xs,
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.danger,
  },
  removeGlyph: {
    transform: [{ rotate: '45deg' }],
  },
  add: {
    width: TILE,
    height: TILE,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    backgroundColor: colors.surfaceAccent,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    borderStyle: 'dashed',
  },
  pressed: {
    opacity: 0.6,
  },
});

export default PhotoStrip;
